import React, { memo } from "react";

import { ShowcaseCard, ShowcaseIntro, ShowcaseNotice } from "ui/components/Showcase";

import { ExportIntroProps } from "./types";

/** Primjer redaka dok nijedno vozilo nije odabrano. */
const SAMPLE_ROWS = [
  { name: "Kia Sorento 2.2 CRDi", plate: "ZG 1234 AB" },
  { name: "Škoda Octavia 2.0 TDI", plate: "ZG 5678 CD" },
  { name: "VW Golf 1.5 TSI", plate: "ST 9012 EF" },
  { name: "Audi A3 1.6 Ambiente", plate: "RI 3456 GH" },
];

/**
 * Lijevi panel izvoza vozila: Excel tablica s odabranim vozilima, broj označenih i povratni uvoz
 * iste datoteke.
 */
const ExportIntro: React.FC<ExportIntroProps> = ({ vehicles, selectedCount }) => {
  const rows =
    vehicles.length > 0
      ? vehicles.map((vehicle) => ({ name: vehicle.name ?? "", plate: vehicle.registrationPlate ?? "" }))
      : SAMPLE_ROWS;

  return (
    <ShowcaseIntro
      sceneKey="export"
      title="Izvoz vozila u Excel"
      description="Označena vozila izvezite u Excel. Datoteku možete urediti i ponovno uvesti u vozni park."
    >
      <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
        <div className="p-5">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-7 h-7 rounded-sm bg-emerald-600 text-[0.6rem] font-bold text-white">
              XLS
            </span>
            <div className="text-sm font-bold text-slate-800 truncate">vozila.xlsx</div>
          </div>

          {/* Excel list: zaglavlje i redovi s nazivom i registracijom. */}
          <div className="mt-3 rounded-sm border border-slate-200 overflow-hidden text-[0.6rem]">
            <div className="grid grid-cols-[1fr_5rem] bg-emerald-50 font-semibold text-emerald-800">
              <span className="px-2 py-1.5 border-r border-slate-200">Vozilo</span>
              <span className="px-2 py-1.5">Registracija</span>
            </div>
            {rows.slice(0, 6).map((row, index) => (
              <div key={index} className="grid grid-cols-[1fr_5rem] border-t border-slate-200 text-slate-600">
                <span className="px-2 py-1.5 border-r border-slate-200 truncate">{row.name}</span>
                <span className="px-2 py-1.5 truncate">{row.plate}</span>
              </div>
            ))}
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Za izvoz</div>
          <div className="mt-2 text-xl font-bold text-slate-800">
            {selectedCount > 0 ? selectedCount : SAMPLE_ROWS.length} vozila
          </div>
          <div className="mt-3 inline-flex px-2 py-0.5 rounded-full text-[0.65rem] font-semibold bg-emerald-100 text-emerald-700">
            Spremno za preuzimanje
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Uredite i vratite"
          subtitle="Istu datoteku uvezite natrag"
          iconClassName="bg-emerald-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M17 1l4 4-4 4M3 11V9a4 4 0 014-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 01-4 4H3" />
            </svg>
          }
        />
      </ShowcaseCard>
    </ShowcaseIntro>
  );
};

export default memo(ExportIntro);
